/**
 * Epic-Ready Structured JSON Export Generator
 * 
 * Produces a comprehensive JSON payload for future system integrations.
 * Includes case metadata, final operative note, CPT lines, authorization
 * references, evidence snippet IDs, attestations, and timestamps.
 */

import type { ExportCptLine, ExportAuditEvent } from './exportPackageGenerator';

export interface EpicJsonPayload {
  /** Schema version */
  version: '2.0';
  exportType: 'epic_ready_handoff';
  isDemoExport: boolean;
  generatedAt: string;
  generatedBy?: string;

  /** Case metadata */
  case: {
    caseId: string;
    caseNumber: string;
    dateOfService?: string;
    laterality?: string;
    facilityName?: string;
    authReference?: string;
    authorizationScope?: string;
    authorizedCpts: string[];
  };

  /** Surgeon information */
  surgeon: {
    name?: string;
    assistantSurgeonName?: string;
    attestation: {
      text?: string;
      timestamp?: string;
      exists: boolean;
    };
  };

  /** Final operative note */
  operativeNote: {
    fullText?: string;
    source: 'dictation' | 'paste' | 'upload' | 'ehr';
    uploadedAt?: string;
  };

  /** Final CPT lines with modifiers */
  cptLines: Array<{
    sequence: number;
    cptCode: string;
    description?: string;
    modifiers: string[];
    units: number;
    finalCodeString: string;
    modifierRationale: Record<string, string>;
  }>;

  /** Evidence references (IDs only — no PHI) */
  evidenceReferences: Array<{
    cptCode: string;
    evidenceSnippetIds: string[];
    documentationStatus: string;
    requirementsSatisfied: number;
    requirementsTotal: number;
  }>;

  /** Surgeon confirmations */
  confirmations: Array<{
    cptCode: string;
    confirmation: string;
    billableStatus: string;
    confirmedAt?: string;
  }>;

  /** Audit timeline */
  auditTimeline: ExportAuditEvent[];

  /** Integrity metadata */
  metadata: {
    closedLoopValidation: 'passed' | 'demo_bypass';
    exportHash: string;
    totalCpts: number;
    totalBillableCpts: number;
    gatesPassed: number;
    gatesTotal: number;
  };
}

export interface EpicJsonInput {
  caseId: string;
  caseNumber: string;
  dateOfService?: string;
  laterality?: string;
  facilityName?: string;
  authReference?: string;
  authorizationScope?: string;
  authorizedCpts: string[];
  surgeonName?: string;
  assistantSurgeonName?: string;
  attestationText?: string;
  attestationTimestamp?: string;
  operativeNoteText?: string;
  operativeNoteSource?: string;
  operativeNoteUploadedAt?: string;
  cptLines: ExportCptLine[];
  evidenceReferences?: Array<{
    cptCode: string;
    evidenceSnippetIds: string[];
    documentationStatus: string;
    requirementsSatisfied: number;
    requirementsTotal: number;
  }>;
  confirmations?: Array<{
    cptCode: string;
    confirmation: string;
    billableStatus: string;
    confirmedAt?: string;
  }>;
  auditTimeline: ExportAuditEvent[];
  gatesPassed: number;
  gatesTotal: number;
  isDemoExport: boolean;
  generatedBy?: string;
}

export function generateEpicJson(input: EpicJsonInput): EpicJsonPayload {
  const generatedAt = new Date().toISOString();

  // Generate integrity hash
  const hashInput = JSON.stringify({
    caseNumber: input.caseNumber,
    cptLines: input.cptLines.map(l => l.finalCodeString),
    generatedAt,
  });
  const exportHash = btoa(hashInput).slice(0, 32);

  const billableCpts = input.cptLines.length;

  return {
    version: '2.0',
    exportType: 'epic_ready_handoff',
    isDemoExport: input.isDemoExport,
    generatedAt,
    generatedBy: input.generatedBy,
    case: {
      caseId: input.isDemoExport ? 'DEMO-REDACTED' : input.caseId,
      caseNumber: input.caseNumber,
      dateOfService: input.dateOfService,
      laterality: input.laterality,
      facilityName: input.facilityName,
      authReference: input.authReference,
      authorizationScope: input.authorizationScope,
      authorizedCpts: input.authorizedCpts,
    },
    surgeon: {
      name: input.surgeonName,
      assistantSurgeonName: input.assistantSurgeonName,
      attestation: {
        text: input.attestationText,
        timestamp: input.attestationTimestamp,
        exists: !!input.attestationText,
      },
    },
    operativeNote: {
      fullText: input.operativeNoteText,
      source: (input.operativeNoteSource || 'paste') as any,
      uploadedAt: input.operativeNoteUploadedAt,
    },
    cptLines: input.cptLines.map(l => ({
      sequence: l.sequence,
      cptCode: l.cptCode,
      description: l.cptDescription,
      modifiers: l.modifiers,
      units: l.units,
      finalCodeString: l.finalCodeString,
      modifierRationale: l.modifierExplanations,
    })),
    evidenceReferences: input.evidenceReferences || [],
    confirmations: input.confirmations || [],
    auditTimeline: input.auditTimeline,
    metadata: {
      closedLoopValidation: input.isDemoExport ? 'demo_bypass' : 'passed',
      exportHash,
      totalCpts: input.authorizedCpts.length,
      totalBillableCpts: billableCpts,
      gatesPassed: input.gatesPassed,
      gatesTotal: input.gatesTotal,
    },
  };
}
